import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, AlertTriangle, CalendarClock, Inbox, Home, Building2, TrendingUp, TrendingUp as Conv, Bell, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { statusBadge, priorityBadge } from "@/pages/admin/AdminShell";

const METRICS = [
  { key: "new_leads", label: "New Leads", icon: Inbox, tone: "text-blue-300" },
  { key: "hot_leads", label: "Hot Leads", icon: Flame, tone: "text-red-300" },
  { key: "followups_due_today", label: "Follow-ups Today", icon: CalendarClock, tone: "text-amber-300" },
  { key: "overdue_followups", label: "Overdue Follow-ups", icon: AlertTriangle, tone: "text-orange-300" },
  { key: "seller_leads", label: "Seller Leads", icon: Home, tone: "text-emerald-300" },
  { key: "agent_leads", label: "Agent Leads", icon: Building2, tone: "text-teal-300" },
  { key: "investor_leads", label: "Investor Leads", icon: TrendingUp, tone: "text-cyan-300" },
  { key: "general_leads", label: "General Enquiries", icon: Inbox, tone: "text-slate-300" },
  { key: "leads_this_week", label: "Leads This Week", icon: Inbox, tone: "text-indigo-300" },
  { key: "leads_this_month", label: "Leads This Month", icon: Inbox, tone: "text-purple-300" },
  { key: "conversion_rate", label: "Conversion Rate", icon: Conv, tone: "text-brass", suffix: "%" },
];

function LeadRow({ lead, onOpen }) {
  return (
    <tr onClick={() => onOpen(lead.lead_id)} data-testid={`lead-row-${lead.lead_id}`} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition">
      <td className="py-3 px-3">
        <div className="font-medium text-white">{lead.full_name}</div>
        <div className="text-xs text-emerald-100/50">{lead.email}</div>
      </td>
      <td className="py-3 px-3 text-emerald-100/70 text-sm hidden md:table-cell">{lead.lead_type}</td>
      <td className="py-3 px-3 text-emerald-100/70 text-sm hidden lg:table-cell">{lead.address || lead.postcode || "—"}</td>
      <td className="py-3 px-3 hidden sm:table-cell"><span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${priorityBadge(lead.priority)}`}>{lead.priority}</span></td>
      <td className="py-3 px-3"><span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${statusBadge(lead.status)}`}>{lead.status}</span></td>
      <td className="py-3 px-3 text-emerald-100/50 text-xs hidden lg:table-cell">{new Date(lead.created_at).toLocaleDateString("en-GB")}</td>
    </tr>
  );
}

export default function CrmLeads() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState({ hot: [], overdue: [], today: [], new: [] });
  const [leads, setLeads] = useState([]);
  const [failed, setFailed] = useState([]);
  const [filters, setFilters] = useState({ lead_type: "", status: "", priority: "", q: "" });
  const [tab, setTab] = useState("followups");
  const [retrying, setRetrying] = useState({});

  const open = (id) => navigate(`/crm/leads/${id}`);

  const loadAll = async () => {
    try {
      const [s, f, fn] = await Promise.all([
        api.get("/leads/stats"), api.get("/leads/followups"), api.get("/failed-notifications"),
      ]);
      setStats(s.data); setFollowups(f.data); setFailed(fn.data.filter((x) => !x.resolved));
    } catch (e) { toast.error("Could not load lead data"); }
  };

  const loadLeads = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await api.get("/leads", { params });
    setLeads(data);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadLeads(); }, [filters]);

  const retry = async (id) => {
    setRetrying((r) => ({ ...r, [id]: true }));
    try {
      await api.post(`/failed-notifications/${id}/retry`);
      toast.success("Retry queued — refreshing shortly");
      setTimeout(loadAll, 6000);
    } catch (e) {
      toast.error("Could not queue retry");
    } finally {
      setTimeout(() => setRetrying((r) => ({ ...r, [id]: false })), 6000);
    }
  };

  const Section = ({ title, list, icon: Icon, tone, testId }) => (
    list.length > 0 && (
      <div data-testid={testId} className="mb-6">
        <div className="flex items-center gap-2 mb-2"><Icon className={`w-4 h-4 ${tone}`} /><h3 className="text-sm font-semibold text-white">{title} <span className="text-emerald-100/50">({list.length})</span></h3></div>
        <div className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm"><tbody>
            {list.map((l) => (
              <tr key={l.lead_id} onClick={() => open(l.lead_id)} data-testid={`followup-row-${l.lead_id}`} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
                <td className="py-2.5 px-3"><span className="font-medium text-white">{l.full_name}</span><span className="text-emerald-100/50 ml-2 text-xs">{l.lead_type}</span></td>
                <td className="py-2.5 px-3 hidden sm:table-cell"><span className={`text-[11px] px-2 py-0.5 rounded-full ${priorityBadge(l.priority)}`}>{l.priority}</span></td>
                <td className="py-2.5 px-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${statusBadge(l.status)}`}>{l.status}</span></td>
                <td className="py-2.5 px-3 text-right">
                  {l.action_required && <span className="text-[11px] text-orange-300 font-medium">ACTION REQUIRED</span>}
                  {!l.action_required && l.untouched_24h && <span className="text-[11px] text-amber-300">Untouched 24h+</span>}
                </td>
              </tr>
            ))}
          </tbody></table>
        </div>
      </div>
    )
  );

  return (
    <CrmShell title="Website Leads">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        {METRICS.map((m) => (
          <div key={m.key} data-testid={`crm-metric-${m.key.replace(/_/g, "-")}`} className="bg-[#0F2E23] rounded-xl border border-white/10 p-4">
            <div className="flex items-center justify-between"><span className="text-xs text-emerald-100/60">{m.label}</span><m.icon className={`w-4 h-4 ${m.tone}`} /></div>
            <div className="mt-2 text-2xl font-mono font-semibold text-white">{stats ? stats[m.key] : "—"}{m.suffix || ""}</div>
          </div>
        ))}
      </div>

      {failed.length > 0 && (
        <div data-testid="failed-notifications" className="mb-8 bg-red-950/40 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3"><Bell className="w-4 h-4 text-red-300" /><h3 className="text-sm font-semibold text-red-200">Failed Notifications ({failed.length})</h3></div>
          <div className="space-y-2">
            {failed.map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-2 text-sm">
                <div className="min-w-0 flex-1"><span className="text-white">{f.kind.replace(/_/g, " ")}</span><span className="text-emerald-100/50 ml-2 text-xs">→ {f.recipient}</span><div className="text-[11px] text-red-300/70 break-words">{f.error}</div></div>
                <button onClick={() => retry(f.id)} disabled={retrying[f.id]} data-testid={`retry-${f.id}`} className="flex items-center gap-1 text-xs text-brass hover:text-brass-500 disabled:opacity-60 shrink-0"><RefreshCw className={`w-3.5 h-3.5 ${retrying[f.id] ? "animate-spin" : ""}`} /> {retrying[f.id] ? "Retrying…" : "Retry"}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("followups")} data-testid="tab-followups" className={`text-sm px-4 py-2 rounded-full transition ${tab === "followups" ? "bg-brass text-emerald-950 font-semibold" : "bg-white/5 text-emerald-100/70"}`}>Today's Follow-ups</button>
        <button onClick={() => setTab("all")} data-testid="tab-all-leads" className={`text-sm px-4 py-2 rounded-full transition ${tab === "all" ? "bg-brass text-emerald-950 font-semibold" : "bg-white/5 text-emerald-100/70"}`}>All Leads</button>
      </div>

      {tab === "followups" && (
        <div>
          {followups.hot.length + followups.overdue.length + followups.today.length + followups.new.length === 0 && (
            <p className="text-emerald-100/50 text-sm py-8 text-center">No follow-ups outstanding. Nicely done.</p>
          )}
          <Section title="Hot leads awaiting contact" list={followups.hot} icon={Flame} tone="text-red-300" testId="followup-hot" />
          <Section title="Overdue follow-ups" list={followups.overdue} icon={AlertTriangle} tone="text-orange-300" testId="followup-overdue" />
          <Section title="Due today" list={followups.today} icon={CalendarClock} tone="text-amber-300" testId="followup-today" />
          <Section title="New leads" list={followups.new} icon={Inbox} tone="text-blue-300" testId="followup-new" />
        </div>
      )}

      {tab === "all" && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            <input placeholder="Search name, email, address…" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="lead-search" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-emerald-100/40 flex-1 min-w-[200px]" />
            <select value={filters.lead_type} onChange={(e) => setFilters((f) => ({ ...f, lead_type: e.target.value }))} data-testid="filter-type" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              <option value="">All types</option>{["Seller", "Estate Agent", "Investor", "General Enquiry"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} data-testid="filter-priority" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              <option value="">All priorities</option>{["Hot", "Warm", "Cold"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="filter-status" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              <option value="">All statuses</option>{["New", "Contact attempted", "Contacted", "Qualified", "Viewing required", "Offer made", "Negotiating", "Under offer", "Completed", "Not suitable", "Nurture", "Closed"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-hidden overflow-x-auto" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-emerald-100/50 border-b border-white/10">
                <th className="py-2.5 px-3 font-medium">Name</th><th className="py-2.5 px-3 font-medium hidden md:table-cell">Type</th>
                <th className="py-2.5 px-3 font-medium hidden lg:table-cell">Property</th><th className="py-2.5 px-3 font-medium hidden sm:table-cell">Priority</th>
                <th className="py-2.5 px-3 font-medium">Status</th><th className="py-2.5 px-3 font-medium hidden lg:table-cell">Received</th>
              </tr></thead>
              <tbody>{leads.map((l) => <LeadRow key={l.lead_id} lead={l} onOpen={open} />)}</tbody>
            </table>
            {leads.length === 0 && <p className="text-emerald-100/50 text-sm py-8 text-center">No leads match your filters.</p>}
          </div>
        </div>
      )}
    </CrmShell>
  );
}
